// Layered model for the route calendar.
//
// A merchandiser's schedule is edited as three ADDITIVE layers (not self-contained buckets):
//   base[wd]            recurring weekly stops for weekday wd (Mon=0..Sun=6)
//   weekAdd[wd][nth]    EXTRA stops layered on top of base for the Nth occurrence of wd
//   dateAdd[YYYY-MM-DD] EXTRA stops layered on top of base(+weekAdd) for one specific date
//
// The stops a merchandiser actually visits on a date = base ++ weekAdd(that Nth) ++ dateAdd,
// concatenated in that order and de-duplicated by point id. So periodic points always apply
// to every day they recur on, and one-off points are pure additions on a single date.
//
// The mobile API still reads one self-contained route per date (GET /routes then /order, no
// date on /order). So on save we MATERIALIZE each occurrence that has additions into a route
// carrying the full merged stop list, keyed so the server filter resolves the right one:
//   base  -> "w{wd}"        (day_of_the_week=wd, week_of_month=NULL)
//   nth   -> "n{wd}_{nth}"  (day_of_the_week=wd, week_of_month=nth) stops = base+weekAdd
//   date  -> "d{date}"      (day_of_the_week=NULL, start/end=date)  stops = base+weekAdd+dateAdd

import { nthOfMonth, weekdayMon0, ymd } from "./dateUtils";

export const baseKey = wd => `w${wd}`;
export const weekKey = (wd, nth) => `n${wd}_${nth}`;
export const dateKey = ds => `d${ds}`;

export const isBaseKey = key => key[0] === "w";
export const isWeekKey = key => key[0] === "n";
export const isDateKey = key => key[0] === "d";

const emptyLayers = () => ({ base: {}, week: {}, date: {} });

// Ordered list of point-stops from a route's nested order_set.
const stopsFromRoute = route =>
  (route.points || [])
    .filter(o => o && o.point)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(o => ({ point: o.point }));

// Classify a loaded route into its materialized-route key.
export const keyForRoute = route => {
  if (route.start) return dateKey(route.start.slice(0, 10));
  if (route.week_of_month != null && route.day_of_the_week != null) return weekKey(route.day_of_the_week, route.week_of_month);
  if (route.day_of_the_week != null) return baseKey(route.day_of_the_week);
  return null;
};

const appendUnique = (arr, stops) => {
  const seen = new Set(arr.map(s => s.point.id));
  stops.forEach(s => {
    if (!seen.has(s.point.id)) {
      arr.push(s);
      seen.add(s.point.id);
    }
  });
};

// Build the editable layers + a snapshot of the routes as loaded (for diffing on save).
// Materialized routes are self-contained (base+adds), so we recover the additive deltas by
// SUBTRACTING the more-general layers — which also migrates any older self-contained data.
// Returns { layers, loaded: {key:{id,pointIds}}, extraDeletedIds }.
export const layersFromRoutes = routes => {
  const layers = emptyLayers();
  const loaded = {};
  const extraDeletedIds = [];

  const list = (routes || []).filter(r => keyForRoute(r));
  const byKind = kindTest => list.filter(r => kindTest(keyForRoute(r)));

  const register = (key, route) => {
    if (!loaded[key]) loaded[key] = { id: route.id || null, pointIds: stopsFromRoute(route).map(s => s.point.id) };
    else if (route.id) extraDeletedIds.push(route.id); // duplicate slot -> delete extra on save
  };

  // 1) base (week_of_month = NULL)
  byKind(isBaseKey).forEach(route => {
    const wd = route.day_of_the_week;
    layers.base[wd] = layers.base[wd] || [];
    appendUnique(layers.base[wd], stopsFromRoute(route));
    register(baseKey(wd), route);
  });

  // 2) Nth-weekday: extras = route stops not already in base[wd]
  byKind(isWeekKey).forEach(route => {
    const wd = route.day_of_the_week;
    const nth = route.week_of_month;
    const baseIds = new Set((layers.base[wd] || []).map(s => s.point.id));
    layers.week[wd] = layers.week[wd] || {};
    layers.week[wd][nth] = layers.week[wd][nth] || [];
    appendUnique(layers.week[wd][nth], stopsFromRoute(route).filter(s => !baseIds.has(s.point.id)));
    register(weekKey(wd, nth), route);
  });

  // 3) one-off date: extras = route stops not already in base(+weekAdd for that occurrence)
  byKind(isDateKey).forEach(route => {
    const ds = route.start.slice(0, 10);
    const [y, m, d] = ds.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    const wd = weekdayMon0(dt);
    const nth = nthOfMonth(dt);
    const covered = new Set([
      ...(layers.base[wd] || []).map(s => s.point.id),
      ...((layers.week[wd] && layers.week[wd][nth]) || []).map(s => s.point.id),
    ]);
    layers.date[ds] = layers.date[ds] || [];
    appendUnique(layers.date[ds], stopsFromRoute(route).filter(s => !covered.has(s.point.id)));
    register(dateKey(ds), route);
  });

  return { layers, loaded, extraDeletedIds };
};

// The stops that apply on a date, in visit order, each tagged with its layer + layerKey
// (used for the badge and to scope removal/reorder). De-duplicated across layers by point id.
export const mergedStopsForDate = (date, layers) => {
  const wd = weekdayMon0(date);
  const nth = nthOfMonth(date);
  const ds = ymd(date);
  const out = [];
  const seen = new Set();
  const take = (arr, layer, layerKey) =>
    (arr || []).forEach(s => {
      if (!seen.has(s.point.id)) {
        seen.add(s.point.id);
        out.push({ point: s.point, layer, layerKey });
      }
    });
  take(layers.base[wd], "base", baseKey(wd));
  take(layers.week[wd] && layers.week[wd][nth], "week", weekKey(wd, nth));
  take(layers.date[ds], "date", dateKey(ds));
  return out;
};

// Route metadata spec for a materialized key (orders are posted separately).
export const routeSpecForKey = (key, { userId, createdBy, title }) => {
  const spec = { title: title || "", user: userId, created_by: createdBy ?? null, day_of_the_week: null, week_of_month: null, start: null, end: null };
  if (isBaseKey(key)) {
    spec.day_of_the_week = Number(key.slice(1));
  } else if (isWeekKey(key)) {
    const [wd, nth] = key.slice(1).split("_").map(Number);
    spec.day_of_the_week = wd;
    spec.week_of_month = nth;
  } else if (isDateKey(key)) {
    const d = key.slice(1);
    spec.start = `${d}T00:00:00`;
    spec.end = `${d}T23:59:59`;
  }
  return spec;
};

const mergeStops = (...lists) => {
  const out = [];
  const seen = new Set();
  lists.forEach(list =>
    (list || []).forEach(s => {
      if (!seen.has(s.point.id)) {
        seen.add(s.point.id);
        out.push({ point: s.point });
      }
    })
  );
  return out;
};

// Every materialized route the layers imply: base per used weekday, an Nth route per
// occurrence that has additions (stops = base+weekAdd), a date route per date with additions
// (stops = base+weekAdd(that Nth)+dateAdd). Returns [{ key, stops:[{point}] }].
export const desiredRoutesFromLayers = layers => {
  const out = [];
  Object.entries(layers.base).forEach(([wd, stops]) => {
    if (stops && stops.length) out.push({ key: baseKey(Number(wd)), stops: stops.slice() });
  });
  Object.entries(layers.week).forEach(([wd, byNth]) =>
    Object.entries(byNth || {}).forEach(([nth, stops]) => {
      if (stops && stops.length) out.push({ key: weekKey(Number(wd), Number(nth)), stops: mergeStops(layers.base[wd], stops) });
    })
  );
  Object.entries(layers.date).forEach(([ds, stops]) => {
    if (stops && stops.length) {
      const [y, m, d] = ds.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      const wd = weekdayMon0(dt);
      const nth = nthOfMonth(dt);
      out.push({ key: dateKey(ds), stops: mergeStops(layers.base[wd], layers.week[wd] && layers.week[wd][nth], stops) });
    }
  });
  return out;
};

export const ordersForRoute = (routeId, stops) =>
  (stops || []).map((s, idx) => ({ route: routeId, point: s.point.id, order: idx }));

// ---- immutable layer edits -------------------------------------------------

const cloneLayers = layers => ({
  base: { ...layers.base },
  week: Object.fromEntries(Object.entries(layers.week).map(([wd, byNth]) => [wd, { ...byNth }])),
  date: { ...layers.date },
});

// Which layer array a scope writes to for a given date.
const containerFor = (layers, scope, wd, nth, ds) => {
  if (scope === "every") return { get: () => layers.base[wd], set: v => (layers.base[wd] = v) };
  if (scope === "nth") {
    layers.week[wd] = { ...(layers.week[wd] || {}) };
    return { get: () => layers.week[wd][nth], set: v => (layers.week[wd][nth] = v) };
  }
  return { get: () => layers.date[ds], set: v => (layers.date[ds] = v) };
};

// True if the point is already effective for `scope` on `date` (avoids cross-layer dupes).
const alreadyPresent = (layers, scope, wd, nth, ds, pointId) => {
  const inArr = arr => (arr || []).some(s => s.point.id === pointId);
  if (inArr(layers.base[wd])) return true;
  if (scope === "every") return false;
  if (inArr(layers.week[wd] && layers.week[wd][nth])) return true;
  if (scope === "nth") return false;
  return inArr(layers.date[ds]);
};

export const addPoint = (layers, scope, date, point) => {
  const wd = weekdayMon0(date);
  const nth = nthOfMonth(date);
  const ds = ymd(date);
  if (alreadyPresent(layers, scope, wd, nth, ds, point.id)) return layers;
  const next = cloneLayers(layers);
  const c = containerFor(next, scope, wd, nth, ds);
  c.set([...(c.get() || []), { point }]);
  return next;
};

const parseLayerKey = key => {
  if (isBaseKey(key)) return { kind: "base", wd: Number(key.slice(1)) };
  if (isWeekKey(key)) {
    const [wd, nth] = key.slice(1).split("_").map(Number);
    return { kind: "week", wd, nth };
  }
  return { kind: "date", ds: key.slice(1) };
};

const layerArray = (layers, layerKey) => {
  const p = parseLayerKey(layerKey);
  if (p.kind === "base") return layers.base[p.wd] || [];
  if (p.kind === "week") return (layers.week[p.wd] && layers.week[p.wd][p.nth]) || [];
  return layers.date[p.ds] || [];
};

const setLayerArray = (layers, layerKey, arr) => {
  const p = parseLayerKey(layerKey);
  if (p.kind === "base") {
    if (arr.length) layers.base[p.wd] = arr;
    else delete layers.base[p.wd];
  } else if (p.kind === "week") {
    layers.week[p.wd] = { ...(layers.week[p.wd] || {}) };
    if (arr.length) layers.week[p.wd][p.nth] = arr;
    else delete layers.week[p.wd][p.nth];
  } else if (arr.length) layers.date[p.ds] = arr;
  else delete layers.date[p.ds];
};

export const removePoint = (layers, layerKey, pointId) => {
  const next = cloneLayers(layers);
  setLayerArray(next, layerKey, layerArray(next, layerKey).filter(s => s.point.id !== pointId));
  return next;
};

// Move a point within its own layer by dir (-1 up, +1 down). Order within a layer = the
// materialized visit order for that layer's block.
export const movePoint = (layers, layerKey, pointId, dir) => {
  const arr = layerArray(layers, layerKey).slice();
  const i = arr.findIndex(s => s.point.id === pointId);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= arr.length) return layers;
  const next = cloneLayers(layers);
  [arr[i], arr[j]] = [arr[j], arr[i]];
  setLayerArray(next, layerKey, arr);
  return next;
};
